import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  formatCount,
  getLocaleMessage,
  localeTag,
} from "./localeCatalog";
import {
  canonicalizeLocaleUrl,
  resolveLocale,
  UI_LANGUAGE_STORAGE_KEY,
} from "./localeRouting";

const LocaleContext = createContext(null);

export function UiLanguageProvider({ children }) {
  const [locale, setLocale] = useState(() => {
    try {
      return resolveLocale({
        pathname: window.location.pathname,
        search: window.location.search,
        storage: window.localStorage,
        navigator: window.navigator,
      }).locale;
    } catch {
      return "th";
    }
  });

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dataset.uiLanguage = locale;
    try {
      window.localStorage.setItem(UI_LANGUAGE_STORAGE_KEY, locale);
      const url = canonicalizeLocaleUrl(window.location, locale);
      if (url.href !== window.location.href) {
        window.history.replaceState({}, "", url);
      }
    } catch {
      // The language switch still works when storage is unavailable.
    }
  }, [locale]);

  const value = useMemo(() => ({
    locale,
    en: locale === "en",
    languageTag: localeTag(locale),
    setLocale: (next) => setLocale(next === "en" ? "en" : "th"),
    text: (key, variables) => getLocaleMessage(locale, key, variables),
    count: (value) => formatCount(value, locale),
  }), [locale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  return useContext(LocaleContext) || {
    locale: "th",
    en: false,
    languageTag: "th-TH-u-ca-buddhist",
    setLocale: () => {},
    text: (key, variables) => getLocaleMessage("th", key, variables),
    count: (value) => formatCount(value, "th"),
  };
}
